import { DeleteOfferConfirmation } from "./modals/delete-offer-confirmation";
import { OfferCardActionButtons } from "./offer-card-action-buttons";
import { OfferCardDetails } from "./offer-card-details";
import { OfferCardLocations } from "./offer-card-locations";
import { OfferCardLogHistoryButton } from "./offer-card-log-history-button";
import { OfferCardMap } from "./offer-card-map";
import { OfferCardRepViewTable } from "./offer-card-rep-view-table";
import { OfferCardTabletDetails } from "./offer-card-tablets-details";
import { RateEntry } from "./modals/rate-entry";
import { RenewOfferConfirmation } from "./modals/renew-offer";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import SlideToggle from "react-slide-toggle";
import eases from "eases";
import { isEqual } from "lodash";
import { useGetUserInfo } from "../../../../../shared/infrastructure/services/user.services";
import {
  CardFlip,
  HandIcon,
  InfoIcon,
  Modal,
  PencilIcon,
  RepeatIcon,
  Tooltip,
  TrashIcon,
  useDisclosure,
} from "../../../../../shared/infrastructure";
import {
  Carrier,
  Customer,
  DataItem,
  MobileDataItems,
  OfferCardCustomerRate,
  OfferCardInformation,
  RepCardInformation,
} from "./offer-card-information";
import { OfferCardSectionT, OfferT, OfferViewT } from "../../../domain";
import { OfferCardTopBar, RepCardTopBar } from "./offer-card-top-bar";
import React, { LegacyRef, useEffect, useState } from "react";

type OfferCardLayoutProps = {
  map: React.ReactNode;
  topBar: React.ReactNode;
  locations: React.ReactNode;
  information: React.ReactNode;
  details: React.ReactNode;
  table?: React.ReactNode;
  showDetails: boolean;
  showEditDeleteOptions?: boolean;
  showRenewOption?: boolean;
  offer: OfferT;
  toggleView?: () => void;
  showHighlighted?: boolean;
  isManaged?: boolean;
  view?: OfferViewT;
  isRemoved?: boolean;
  tableViewIsOpen?: boolean;
  isDedicated: boolean;
  toggleTable?: () => void;
};

const OfferCardLayout = ({
  map,
  topBar,
  locations,
  tableViewIsOpen,
  toggleTable,
  information,
  details,
  toggleView,
  isDedicated,
  table,
  offer,
  showDetails,
  view = "offer",
  showEditDeleteOptions = false,
  showRenewOption = false,
  showHighlighted = false,
  isManaged = false,
  isRemoved = false,
}: OfferCardLayoutProps): React.ReactElement => {
  const {
    isOpen: isUpdateOpen,
    close: closeUpdate,
    open: openUpdate,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    close: closeDelete,
    open: openDelete,
  } = useDisclosure();
  const {
    isOpen: isRenewOpen,
    close: closeRenew,
    open: openRenew,
  } = useDisclosure();

  const {
    isOpen: isOpenEquipmentDetails,
    close: closeEquipmentDetails,
    open: openEquipmentDetails,
  } = useDisclosure();

  const {
    isOpen: isOpenCustomerDetails,
    close: closeCustomerDetails,
    open: openCustomerDetails,
  } = useDisclosure();

  return (
    <section
      className={[
        "group relative mx-auto max-w-[335px] rounded-[5px] md:max-w-[648px] lg:max-w-[906px] xl:max-w-none",
        showHighlighted ? "border-[2px] border-solid border-[#2196F3]" : "",
        isManaged || isRemoved ? "bg-transparent" : "bg-white",
        isDedicated && "pb-5 md:pb-0",
      ].join(" ")}
    >
      <section
        className={[
          "relative flex flex-col md:grid md:gap-4 md:p-3  xl:grid-cols-[200px_1fr_1px_200px] xl:p-4 2xl:grid-cols-[200px_1fr_1px_250px]",
          "md:grid-cols-[120px_1fr] lg:grid-cols-[150px_1fr_1px_200px]",
          isManaged || isRemoved
            ? "rounded-[5px] border-[2px] border-solid border-[#E3E3E3] opacity-[.5]"
            : "",
        ].join(" ")}
      >
        {/* Mobile only */}
        <div
          className={[
            showDetails ? "flex md:hidden" : "hidden",
            "items-center gap-x-3 px-4 pt-4 pb-8",
          ].join(" ")}
        >
          <h3 className="text-sm font-medium text-black">
            {offer?.customer?.name}
          </h3>
          <span className="text-[10px] text-[#383F48]">
            {offer?.customer?.customer_id}
          </span>
        </div>
        {map}
        <section
          className={[
            showDetails ? "hidden" : "flex",
            "flex-col gap-y-5 px-4 md:px-0 lg:gap-y-8 xl:grid xl:grid-rows-[auto_1fr_auto] xl:gap-[16px] xl:pl-[16px]",
          ].join(" ")}
        >
          {topBar}
          {locations}
          {information}

          {/* 1024px devices only */}
          <div className="col-span-full hidden lg:flex xl:hidden">
            <div className="flex w-full items-center justify-between lg:mt-[14px] lg:w-auto">
              <section className="flex gap-x-2">
                <div className="flex flex-col lg:flex-row">
                  <div className="flex items-center gap-x-1 ">
                    <InfoIcon onClick={openEquipmentDetails} />
                    <DataItem
                      label="equipment-types"
                      value={offer.equipment_type?.description ?? "- -"}
                    />
                  </div>

                  <span className="mr-2 hidden w-[1px] self-center bg-[#D6D6D6] lg:inline-block lg:h-[12px]" />

                  <div className="flex items-center gap-x-1">
                    <InfoIcon onClick={openCustomerDetails} />
                    <Customer
                      className="hidden md:flex md:items-center md:gap-x-2"
                      offer={offer}
                      customerId={offer.customer?.customer_id ?? "- -"}
                      customerName={offer.customer?.name ?? "- -"}
                      rate={offer.rate}
                    />
                  </div>
                </div>

                <span className="h-[32px] w-[1px] self-center bg-[#D6D6D6] lg:h-[12px]" />

                <div className="flex">
                  <OfferCardCustomerRate
                    offer={offer}
                    rate={offer.rate}
                    showDetails={showDetails}
                    salesPersonName={offer.customer?.salesperson?.name || "- -"}
                  />
                </div>
              </section>

              <div className="lg:hidden">{details}</div>
            </div>
          </div>
        </section>

        {/* Tablet only view to show details */}
        <section
          className={[showDetails ? "flex lg:hidden" : "hidden"].join(" ")}
        >
          <OfferCardTabletDetails offer={offer} />
        </section>

        {/* Mobile only */}
        <section
          className={[
            showDetails ? "flex md:hidden" : "hidden",
            "flex-col gap-y-4 px-4",
          ].join(" ")}
        >
          {information}
        </section>

        <span className="line-vertical md:hidden lg:block" />

        {/* Mobile only */}
        <div className="px-4 py-4 md:hidden">
          <OfferCardCustomerRate
            offer={offer}
            rate={offer?.rate}
            showDetails={showDetails}
            salesPersonName={offer?.customer?.salesperson?.name || "- -"}
          >
            <OfferCardLogHistoryButton offer={offer} />
          </OfferCardCustomerRate>
        </div>

        <div className="md:hidden lg:inline-flex lg:flex-col 2xl:hidden">
          {details}
        </div>

        {/* large desktop */}
        <div className="hidden 2xl:inline-flex">
          <div className="flex flex-col">
            <OfferCardTabletDetails offer={offer}>
              <OfferCardActionButtons
                offer={offer}
                toggleView={toggleView}
                view={view}
                tableViewIsOpen={tableViewIsOpen}
                toggleTable={toggleTable}
              />
            </OfferCardTabletDetails>
          </div>
        </div>

        {/* Tablet Only */}
        <div className="col-span-full hidden md:flex lg:hidden">
          <div className="flex w-full items-center justify-between lg:absolute lg:top-[156px] lg:left-[180px] lg:w-auto">
            <section className="flex gap-x-2">
              <div className="flex flex-col lg:flex-row">
                <div className="flex items-center gap-x-1 ">
                  <InfoIcon onClick={openEquipmentDetails} />
                  <DataItem
                    label="equipment-types"
                    value={offer.equipment_type?.description ?? "- -"}
                  />
                </div>

                <span className="mr-2 hidden w-[1px] self-center bg-[#D6D6D6] lg:inline-block lg:h-[12px]" />

                <div className="flex items-center gap-x-1">
                  <InfoIcon onClick={openCustomerDetails} />
                  <Customer
                    className="hidden md:flex md:items-center md:gap-x-2"
                    offer={offer}
                    customerId={offer.customer?.customer_id ?? "- -"}
                    customerName={offer.customer?.name ?? "- -"}
                    rate={offer.rate}
                  />
                </div>
              </div>

              <span className="h-[32px] w-[1px] self-center bg-[#D6D6D6] lg:h-[12px]" />

              <div className="flex">
                <OfferCardCustomerRate
                  offer={offer}
                  rate={offer.rate}
                  showDetails={showDetails}
                  salesPersonName={offer.customer?.salesperson?.name || "- -"}
                />
              </div>
            </section>

            <div className="lg:hidden">{details}</div>
          </div>
        </div>

        {/*  */}
        {showEditDeleteOptions && !isManaged && !isRemoved && (
          <section className="absolute top-[38px] right-0 z-[-1000] grid h-[calc(100%-76px)] grid-flow-col transition-all duration-[150] group-hover:right-[-32px]">
            <section className="h-full w-[6px] bg-transparent" />
            <section className="grid w-[26px] content-center gap-[40px] rounded-[5px] bg-[#FFFFFF] p-[5px]">
              <Tooltip text="Edit">
                <button onClick={openUpdate}>
                  <PencilIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
              <Tooltip text="Delete">
                <button onClick={openDelete}>
                  <TrashIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
            </section>
          </section>
        )}

        {showRenewOption && (
          <section className="absolute top-[38px] right-0 z-[-1000] grid h-[calc(100%-76px)] grid-flow-col transition-all duration-[150] group-hover:right-[-32px]">
            <section className="h-full w-[6px] bg-transparent" />
            <section className="grid w-[26px] content-center gap-[40px] rounded-[5px] bg-[#FFFFFF] p-[5px]">
              <Tooltip text="Renew">
                <button onClick={openRenew}>
                  <RepeatIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
            </section>
          </section>
        )}
      </section>
      {table ?? null}
      {showEditDeleteOptions && !isManaged && (
        <React.Fragment>
          <Modal
            open={isUpdateOpen}
            onClose={closeUpdate}
            content={<RateEntry onClose={closeUpdate} offer={offer} />}
            classNames={{
              content: "overflow-y-auto overflow-x-hidden p-0",
              paper: "rounded-[5px] min-w-[362px]",
            }}
          />
          <Modal
            open={isDeleteOpen}
            onClose={closeDelete}
            content={
              <DeleteOfferConfirmation close={closeDelete} data={offer} />
            }
            classNames={{
              content: "overflow-hidden p-[20px] w-[350px]",
              paper: "rounded-[5px]",
            }}
          />
        </React.Fragment>
      )}
      {isOpenEquipmentDetails && (
        <Modal
          open={isOpenEquipmentDetails}
          onClose={closeEquipmentDetails}
          content={
            <MobileDataItems
              weight={offer.weight}
              equipment_type={offer.equipment_type}
              revenue_code_id={offer.revenue_code_id}
              ordered_date={offer.ordered_date}
            />
          }
          classNames={{
            content: "overflow-hidden p-4 w-[388px]",
            paper: "rounded-[5px]",
          }}
        />
      )}
      {isOpenCustomerDetails && (
        <Modal
          open={isOpenCustomerDetails}
          onClose={closeCustomerDetails}
          content={
            <Carrier
              carrierId={offer.carrier.carrier_id ?? "- -"}
              carrierName={offer.carrier.name ?? "- -"}
              carrierHighwayId={offer.carrier.highway_id ?? ""}
              carrierMcNumber={
                !offer.carrier.mc_number ? "- -" : offer.carrier.mc_number
              }
              carrierDotNumber={
                !offer.carrier.dot_number ? "- -" : offer.carrier.dot_number
              }
            />
          }
          classNames={{
            content: "overflow-hidden p-4 w-[350px]",
            paper: "rounded-[5px]",
          }}
        />
      )}
      {showRenewOption && (
        <React.Fragment>
          <Modal
            open={isRenewOpen}
            onClose={closeRenew}
            content={<RenewOfferConfirmation close={closeRenew} data={offer} />}
            classNames={{
              content: "overflow-hidden p-[20px] w-[350px]",
              paper: "rounded-[5px]",
            }}
          />
        </React.Fragment>
      )}
      {isManaged && (
        <section className="absolute top-0 left-0 grid h-full w-full place-content-center">
          <section className="grid grid-flow-col items-center gap-[5px] rounded-[5px] border-[2px] border-solid border-[#9E9E9E] bg-[#FFFFFF] p-[14px] text-[16px] font-[700] text-[#0C1F3D]">
            <HandIcon width={24} height={24} />
            <span>This offer has already been managed</span>
          </section>
        </section>
      )}
      {isRemoved && (
        <section className="absolute top-0 left-0 grid h-full w-full place-content-center">
          <section className="grid grid-flow-col items-center gap-[5px] rounded-[5px] border-[2px] border-solid border-[#9E9E9E] bg-[#FFFFFF] p-[14px] text-[16px] font-[700] text-[#0C1F3D]">
            <TrashIcon width={24} height={24} />
            <span>This offer was deleted</span>
          </section>
        </section>
      )}
    </section>
  );
};

type OfferCardProps = {
  data: OfferT;
  cardType: OfferCardSectionT;
  showHighlighted?: boolean;
  isManaged?: boolean;
  isRemoved?: boolean;
};

const OfferCardComponent = ({
  data,
  cardType,
  showHighlighted = false,
  isManaged = false,
  isRemoved = false,
}: OfferCardProps): React.ReactElement => {
  const { data: user } = useGetUserInfo();

  const [view, setView] = useState<OfferViewT>(
    user?.default_lists_view ?? "offer"
  );
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (user?.default_lists_view) {
      setView(user?.default_lists_view);
    }
  }, [user]);

  useEffect(() => {
    // Function to update the showDetails state based on the screen width
    const updateShowDetails = (): void => {
      if (window.innerWidth >= 1024) {
        setShowDetails(false);
      }
    };

    // Call the function on the initial component mount
    updateShowDetails();

    // Add a listener for the window resize event
    window.addEventListener("resize", updateShowDetails);

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("resize", updateShowDetails);
    };
  }, []);

  const toggleDetails = (): void => {
    setShowDetails((prev) => !prev);
  };

  return (
    <div id={`offer-card-${data.approval_id}`}>
      <CardFlip
        isFlipped={view === "rep" && !isManaged && !isRemoved}
        flipSpeedFrontToBack={800}
        flipSpeedBackToFront={800}
      >
        <OfferCardLayout
          showDetails={showDetails}
          showHighlighted={showHighlighted}
          isManaged={isManaged}
          view="offer"
          isRemoved={isRemoved}
          offer={data}
          isDedicated={data.type === "dedicated"}
          toggleView={() => !isManaged && setView("rep")}
          showEditDeleteOptions={cardType === "queue"}
          showRenewOption={cardType === "history" && data.status === "rejected"}
          map={
            <OfferCardMap
              showDetails={showDetails}
              offer={data}
              showLabel
              view="offer"
            />
          }
          topBar={<OfferCardTopBar data={data} cardType={cardType} />}
          locations={<OfferCardLocations offer={data} showLogHistory />}
          information={
            <OfferCardInformation
              showDetails={showDetails}
              offer={data}
              isDedicated={data.type === "dedicated"}
            />
          }
          details={
            <OfferCardDetails
              offer={data}
              showDetails={showDetails}
              toggleDetails={toggleDetails}
              view="offer"
              toggleView={() => !isManaged && setView("rep")}
            />
          }
        />

        {!isManaged && data.type !== "dedicated" ? (
          <SlideToggle
            duration={500}
            easeCollapse={eases["quintOut"]}
            easeExpand={eases["quintOut"]}
            render={({
              onToggle,
              setCollapsibleElement,
              toggleState,
            }: {
              onToggle: () => void;
              setCollapsibleElement: LegacyRef<HTMLDivElement>;
              toggleState: string;
            }) => (
              <OfferCardLayout
                showDetails={showDetails}
                showHighlighted={showHighlighted}
                offer={data}
                view="rep"
                isDedicated={data.type === "dedicated"}
                tableViewIsOpen={
                  toggleState === "EXPANDED" || toggleState === "EXPANDING"
                }
                toggleTable={onToggle}
                toggleView={() => setView("offer")}
                map={
                  <OfferCardMap
                    showDetails={showDetails}
                    offer={data}
                    showLabel
                    view="offer"
                  />
                }
                topBar={<RepCardTopBar data={data} />}
                locations={<OfferCardLocations offer={data} showLogHistory />}
                information={
                  <RepCardInformation showDetails={showDetails} offer={data} />
                }
                details={
                  <OfferCardDetails
                    offer={data}
                    showDetails={showDetails}
                    toggleDetails={toggleDetails}
                    view="rep"
                    tableViewIsOpen={
                      toggleState === "EXPANDED" || toggleState === "EXPANDING"
                    }
                    toggleTable={onToggle}
                    toggleView={() => setView("offer")}
                  />
                }
                table={
                  <section ref={setCollapsibleElement}>
                    <OfferCardRepViewTable
                      offer={data}
                      isOpen={view === "rep"}
                    />
                  </section>
                }
              />
            )}
          />
        ) : (
          <section />
        )}
      </CardFlip>
    </div>
  );
};

export const OfferCard = React.memo(
  OfferCardComponent,
  (prevProps: OfferCardProps, nextProps: OfferCardProps) =>
    prevProps.data.approval_id === nextProps.data.approval_id &&
    prevProps.cardType === nextProps.cardType &&
    prevProps.showHighlighted === nextProps.showHighlighted &&
    prevProps.isManaged === nextProps.isManaged &&
    prevProps.isRemoved === nextProps.isRemoved &&
    isEqual(prevProps.data, nextProps.data)
);
